import { Link, useParams } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card, EmptyState, PageHeader } from '../components/ui.js';
import { guidesFor } from '../help/guides.js';
import { useAppMeta } from '../lib/meta.js';
import { useExtension, useSlot } from '../lib/slots.js';

/**
 * Help: short guides to each screen (`/help`), written once in the core for every deploy,
 * followed by the client's own pages from the `app.help` slot (`/help/x/<id>`).
 */

function GuideLink({
  to,
  params,
  title,
  summary,
}: {
  to: '/help/$guide' | '/help/x/$guide';
  params: { guide: string };
  title: string;
  summary: string;
}) {
  return (
    <li>
      <Link
        to={to}
        params={params}
        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 focus-visible:bg-slate-50"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-slate-900">{title}</span>
          <span className="block text-sm text-slate-500">{summary}</span>
        </span>
        <ChevronRight className="size-4 shrink-0 text-slate-400" aria-hidden />
      </Link>
    </li>
  );
}

export function HelpIndex() {
  const meta = useAppMeta();
  const extension = useExtension();
  const clientGuides = useSlot('app.help').filter((g) => g.when?.(meta) ?? true);
  const clientName = meta.client?.name ?? extension?.client ?? 'Your business';
  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Help"
        subtitle={`Short guides to ${meta.branding.product_name}. Each shows only what you can use.`}
      />
      <div className="space-y-5">
        <Card title="Guides" padded={false}>
          <ul className="divide-y divide-slate-100">
            {guidesFor(meta).map((guide) => (
              <GuideLink
                key={guide.id}
                to="/help/$guide"
                params={{ guide: guide.id }}
                title={guide.title}
                summary={guide.summary}
              />
            ))}
          </ul>
        </Card>
        {clientGuides.length > 0 && (
          <Card title={clientName} padded={false}>
            <ul className="divide-y divide-slate-100">
              {clientGuides.map((guide) => (
                <GuideLink
                  key={guide.id}
                  to="/help/x/$guide"
                  params={{ guide: guide.id }}
                  title={guide.title}
                  summary={guide.summary}
                />
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

function GuidePage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl">
      <PageHeader
        eyebrow={
          <Link to="/help" className="hover:text-slate-700">
            Help
          </Link>
        }
        title={title}
      />
      <Card>{children}</Card>
    </div>
  );
}

function NoGuide() {
  return (
    <EmptyState title="There is no guide here">
      The link may be out of date.{' '}
      <Link to="/help" className="font-medium text-brand-strong hover:underline">
        See all the guides
      </Link>
      .
    </EmptyState>
  );
}

export function HelpGuide() {
  const { guide: id } = useParams({ strict: false }) as { guide: string };
  const meta = useAppMeta();
  const guide = guidesFor(meta).find((g) => g.id === id);
  if (!guide) return <NoGuide />;
  const Body = guide.component;
  return (
    <GuidePage title={guide.title}>
      <Body meta={meta} />
    </GuidePage>
  );
}

export function ClientHelpGuide() {
  const { guide: id } = useParams({ strict: false }) as { guide: string };
  const meta = useAppMeta();
  const guide = useSlot('app.help').find((g) => g.id === id && (g.when?.(meta) ?? true));
  if (!guide) return <NoGuide />;
  const Body = guide.component;
  return (
    <GuidePage title={guide.title}>
      <Body />
    </GuidePage>
  );
}
